import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import AuthContext from "../auth/AuthContext";
import AuthAvatar from "../components/AuthAvatar";
import ManagePostsPanel from "../components/ManagePostsPanel";
import SiteSettingsPanel from "../components/SiteSettingsPanel";
import TopicVisibilityPanel from "../components/TopicVisibilityPanel";
import BankAccountsPanel from "../components/finance/BankAccountsPanel";
import {
  getInvitationCodes,
  postInviteUser,
  postResetInvitePassword,
  revokeInvitationCode,
  uploadAvatar,
} from "../utils/apiClient";
import useDocumentTitle from "../hooks/useDocumentTitle";
import { invalidateMediaBlob } from "../utils/mediaUrl";

function Profile() {
  const { user, setUser } = useContext(AuthContext);
  useDocumentTitle("Profil");
  const [searchParams, setSearchParams] = useSearchParams();
  const tabs = useMemo(() => {
    const next = ["profile"];
    if (user?.is_admin) {
      next.push("posts", "invite", "visibility", "bank");
      next.push("posts", "invite", "site", "visibility");
    }
    return next;
  }, [user?.is_admin]);

  const tabFromUrl = searchParams.get("tab") || "profile";
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    setActiveTab(tabs.includes(tabFromUrl) ? tabFromUrl : "profile");
  }, [tabFromUrl, tabs]);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteResult, setInviteResult] = useState("");
  const [inviteCodes, setInviteCodes] = useState([]);
  const [inviteCodesLoading, setInviteCodesLoading] = useState(false);
  const [revokeError, setRevokeError] = useState("");
  const [revokingCodeUuid, setRevokingCodeUuid] = useState("");
  const [resettingCodeUuid, setResettingCodeUuid] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  const safeActiveTab = tabs.includes(activeTab) ? activeTab : "profile";

  const setTab = (value) => {
    setActiveTab(value);
    const next = new URLSearchParams(searchParams);
    if (value === "profile") next.delete("tab");
    else next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  const loadInviteCodes = async () => {
    setInviteCodesLoading(true);
    setRevokeError("");
    try {
      const data = await getInvitationCodes();
      setInviteCodes(Array.isArray(data) ? data : []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setInviteError(typeof detail === "string" ? detail : err.message || "Failed to load invites");
    } finally {
      setInviteCodesLoading(false);
    }
  };

  const handleInvite = async (event) => {
    event.preventDefault();
    setInviting(true);
    setInviteError("");
    setInviteResult("");
    try {
      await postInviteUser(inviteEmail.trim());
      setInviteResult(`Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
      setInviteModalOpen(false);
      await loadInviteCodes();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setInviteError(typeof detail === "string" ? detail : err.message || "Invite failed");
    } finally {
      setInviting(false);
    }
  };

  const handleRevokeInvite = async (codeUuid, { deleteUser = false, email = "" } = {}) => {
    const confirmText = deleteUser
      ? `Slet brugeren${email ? ` (${email})` : ""}? Du kan derefter invitere e-mailen igen.`
      : "Tilbagekald denne invitation?";
    if (!window.confirm(confirmText)) return;
    setRevokingCodeUuid(codeUuid);
    setRevokeError("");
    setInviteResult("");
    try {
      await revokeInvitationCode(codeUuid);
      setInviteResult(
        deleteUser
          ? `Bruger slettet${email ? `: ${email}` : ""}. Du kan invitere igen.`
          : "Invitation tilbagekaldt."
      );
      await loadInviteCodes();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setRevokeError(typeof detail === "string" ? detail : err.message || "Revoke failed");
    } finally {
      setRevokingCodeUuid("");
    }
  };

  const handleResetPassword = async (codeUuid, email = "") => {
    const confirmText = `Nulstil adgangskode for ${email || "brugeren"}? Der sendes en ny kode på e-mail, og den gamle adgangskode virker ikke længere.`;
    if (!window.confirm(confirmText)) return;
    setResettingCodeUuid(codeUuid);
    setRevokeError("");
    setInviteResult("");
    try {
      await postResetInvitePassword(codeUuid);
      setInviteResult(
        `Nulstillingsmail sendt${email ? ` til ${email}` : ""}. Brugeren kan sætte en ny adgangskode via linket.`
      );
      await loadInviteCodes();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setRevokeError(typeof detail === "string" ? detail : err.message || "Reset failed");
    } finally {
      setResettingCodeUuid("");
    }
  };

  const handleAvatarChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setAvatarUploading(true);
    setAvatarError("");
    try {
      const updated = await uploadAvatar(file);
      if (user?.avatar_url) invalidateMediaBlob(user.avatar_url);
      if (updated?.avatar_url) invalidateMediaBlob(updated.avatar_url);
      setUser(updated);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setAvatarError(typeof detail === "string" ? detail : err.message || "Upload failed");
    } finally {
      setAvatarUploading(false);
    }
  };

  useEffect(() => {
    if (safeActiveTab === "invite" && user?.is_admin) {
      loadInviteCodes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeActiveTab, user?.is_admin]);

  if (!user) {
    return <Typography color="text.secondary">Indlæser profil…</Typography>;
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        Profil
      </Typography>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={safeActiveTab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab value="profile" label="Profil" />
          {user.is_admin ? <Tab value="posts" label="Administrer indlæg" /> : null}
          {user.is_admin ? <Tab value="invite" label="Inviter brugere" /> : null}
          {user.is_admin ? <Tab value="site" label="Site Settings" /> : null}
          {user.is_admin ? <Tab value="visibility" label="Synlighed" /> : null}
          {user.is_admin ? <Tab value="bank" label="Bank" /> : null}
        </Tabs>
      </Box>

      {safeActiveTab === "profile" ? (
        <Box>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <AuthAvatar
              src={user.avatar_url || ""}
              alt={user.username}
              sx={{ width: 72, height: 72 }}
            >
              {(user.username || "?").slice(0, 1).toUpperCase()}
            </AuthAvatar>
            <Box>
              <Button variant="outlined" component="label" disabled={avatarUploading}>
                {avatarUploading ? "Uploader…" : user.avatar_url ? "Skift foto" : "Vælg foto"}
                <input
                  hidden
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,.heic,.heif"
                  onChange={handleAvatarChange}
                />
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.75 }}>
                JPEG, PNG, GIF eller WebP. Maks. 5 MB.
              </Typography>
              {avatarError ? (
                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                  {avatarError}
                </Typography>
              ) : null}
            </Box>
          </Stack>

          <Box
            component="dl"
            sx={{
              m: 0,
              display: "grid",
              gridTemplateColumns: "140px 1fr",
              rowGap: 1.5,
              columnGap: 2,
              "& dt": { m: 0, color: "text.secondary", fontWeight: 500 },
              "& dd": { m: 0 },
            }}
          >
            <dt>Brugernavn</dt>
            <dd>{user.username}</dd>
            <dt>Email</dt>
            <dd>{user.email}</dd>
            <dt>Status</dt>
            <dd>{user.status}</dd>
            <dt>Admin</dt>
            <dd>{user.is_admin ? "Admin" : "Almindelig bruger"}</dd>
          </Box>
        </Box>
      ) : null}

      {safeActiveTab === "posts" && user.is_admin ? <ManagePostsPanel /> : null}

      {safeActiveTab === "invite" && user.is_admin ? (
        <Box sx={{ maxWidth: 980 }}>
          <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Inviter brugere
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send invitationer via e-mail. Ved glemt adgangskode: nulstil her (ny kode sendes). Kun slet hvis
            kontoen skal væk helt.
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Button variant="contained" onClick={() => setInviteModalOpen(true)}>
              Tilføj bruger
            </Button>
            <Button variant="outlined" onClick={loadInviteCodes} disabled={inviteCodesLoading}>
              Opdater liste
            </Button>
          </Box>
          {inviteError ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {inviteError}
            </Typography>
          ) : null}
          {inviteResult ? (
            <Typography color="success.main" variant="body2" sx={{ mt: 1 }}>
              {inviteResult}
            </Typography>
          ) : null}
          {revokeError ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {revokeError}
            </Typography>
          ) : null}
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>Bruger</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Brugt</TableCell>
                  <TableCell align="right">Handlinger</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inviteCodesLoading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={22} />
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : inviteCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        Ingen invitationskoder endnu.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  inviteCodes.map((row) => (
                    <TableRow key={row.code_uuid}>
                      <TableCell>{row.email}</TableCell>
                      <TableCell>{row.username || "–"}</TableCell>
                      <TableCell>
                        {row.used
                          ? row.user_uuid
                            ? "Registreret"
                            : "Brugt (ingen bruger)"
                          : "Afventer"}
                      </TableCell>
                      <TableCell>
                        {row.used_at ? new Date(row.used_at).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {!row.used ? (
                          <Button
                            color="error"
                            size="small"
                            disabled={revokingCodeUuid === row.code_uuid}
                            onClick={() => handleRevokeInvite(row.code_uuid)}
                          >
                            Tilbagekald
                          </Button>
                        ) : row.user_uuid ? (
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Button
                              size="small"
                              disabled={resettingCodeUuid === row.code_uuid}
                              onClick={() => handleResetPassword(row.code_uuid, row.email)}
                            >
                              Nulstil adgangskode
                            </Button>
                            <Button
                              color="error"
                              size="small"
                              disabled={revokingCodeUuid === row.code_uuid}
                              onClick={() =>
                                handleRevokeInvite(row.code_uuid, {
                                  deleteUser: true,
                                  email: row.email,
                                })
                              }
                            >
                              Slet bruger
                            </Button>
                          </Stack>
                        ) : (
                          <Button
                            color="error"
                            size="small"
                            disabled={revokingCodeUuid === row.code_uuid}
                            onClick={() =>
                              handleRevokeInvite(row.code_uuid, {
                                deleteUser: true,
                                email: row.email,
                              })
                            }
                          >
                            Slet
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}

      {safeActiveTab === "site" && user.is_admin ? <SiteSettingsPanel /> : null}

      {safeActiveTab === "visibility" && user.is_admin ? <TopicVisibilityPanel /> : null}
      {safeActiveTab === "bank" && user.is_admin ? <BankAccountsPanel /> : null}

      <Dialog open={inviteModalOpen} onClose={() => setInviteModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Tilføj brugerinvitation</DialogTitle>
        <DialogContent>
          <Box component="form" id="invite-user-form" onSubmit={handleInvite} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              fullWidth
              type="email"
              label="Email"
              placeholder="bruger@eksempel.dk"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteModalOpen(false)} disabled={inviting}>
            Annuller
          </Button>
          <Button type="submit" form="invite-user-form" variant="contained" disabled={inviting}>
            {inviting ? "Sender…" : "Send invitation"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;
